import { IT_VERTICALS, IT_VERTICAL_SHORT_LABEL, formatOfferVersion } from '@devizy/shared';
import { Skeleton, cn } from '@devizy/ui';
import {
  CaretLeft,
  CaretRight,
  MagnifyingGlass,
  Megaphone,
  PencilSimple,
  Plus,
  Rows,
  Scales,
  Sparkle,
  SquaresFour,
} from '@phosphor-icons/react';
import { keepPreviousData } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import * as React from 'react';

import { BidsCount } from '@/components/bid/bids-count';
import { EmptyList } from '@/components/dashboard/empty-list';
import {
  DataTable,
  StatusBadge,
  TableActionLink,
  TableTag,
  type DataTableColumn,
  type StatusTone,
} from '@/components/data-table';
import { trpc } from '@/lib/trpc/client';

import type { OfferStatus } from '@devizy/db';

interface OfferRow {
  id: string;
  title: string | null;
  status: OfferStatus;
  vertical: string;
  currentVersion: number;
  currentVersionMinor: number;
  isPrivate: boolean;
  createdAt: Date;
  requesterEmail: string;
  bidsCount: number;
}

interface OffersSearch {
  statut?: string;
  q?: string;
  page?: number;
  view?: string;
}

const PAGE_SIZE = 12;

type FilterKey = 'all' | 'draft' | 'published';
type ViewKey = 'table' | 'grid';

function toFilterKey(value: string | undefined): FilterKey {
  if (value === 'draft' || value === 'published') return value;
  return 'all';
}

function toViewKey(value: string | undefined): ViewKey {
  return value === 'grid' ? 'grid' : 'table';
}

const FILTERS = [
  { key: 'all', label: 'Toutes' },
  { key: 'draft', label: 'Brouillons' },
  { key: 'published', label: 'Publiées' },
] as const;

const VERTICAL_PALETTE = [
  '#2563eb',
  '#7c3aed',
  '#0891b2',
  '#d97706',
  '#059669',
  '#db2777',
  '#4f46e5',
  '#0d9488',
];

function verticalDot(vertical: string): string {
  const index = (IT_VERTICALS as readonly string[]).indexOf(vertical);
  return VERTICAL_PALETTE[(index < 0 ? 0 : index) % VERTICAL_PALETTE.length]!;
}

function verticalLabel(vertical: string): string {
  return (IT_VERTICAL_SHORT_LABEL as Record<string, string>)[vertical] ?? vertical;
}

function statusMeta(status: OfferStatus): { label: string; tone: StatusTone } {
  switch (status) {
    case 'DRAFT':
      return { label: 'Brouillon', tone: 'neutral' };
    case 'PUBLISHED':
      return { label: 'Publiée', tone: 'success' };
    case 'SELECTION_IN_PROGRESS':
      return { label: 'Sélection', tone: 'primary' };
    case 'AWAITING_PAYMENT':
      return { label: 'Paiement en attente', tone: 'warning' };
    case 'PAYMENT_ISSUE':
      return { label: 'Problème de paiement', tone: 'danger' };
    case 'ACTIVE':
      return { label: 'Active', tone: 'primary' };
    case 'EXPIRED_NO_SELECTION':
      return { label: 'Expirée', tone: 'neutral' };
    case 'CLOSED_NO_WINNER':
      return { label: 'Clôturée', tone: 'neutral' };
    case 'ARCHIVED':
      return { label: 'Archivée', tone: 'neutral' };
    default:
      return { label: String(status), tone: 'neutral' };
  }
}

function actionFor(
  status: OfferStatus,
  id: string,
): { href: string; label: string; tone: 'default' | 'primary' } {
  if (status === 'DRAFT')
    return { href: `/offers/new?draftId=${id}`, label: 'Continuer', tone: 'default' };
  if (status === 'PUBLISHED' || status === 'SELECTION_IN_PROGRESS') {
    return { href: `/offers/${id}/selection`, label: 'Candidatures', tone: 'primary' };
  }
  return { href: `/offers/${id}`, label: 'Voir', tone: 'default' };
}

function pageItems(page: number, pageCount: number): Array<number | 'gap-start' | 'gap-end'> {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const anchors = [1, page - 1, page, page + 1, pageCount].filter((p) => p >= 1 && p <= pageCount);
  const sorted = [...new Set(anchors)].sort((a, b) => a - b);
  const out: Array<number | 'gap-start' | 'gap-end'> = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev > 0 && p - prev > 1) out.push(p > page ? 'gap-end' : 'gap-start');
    out.push(p);
    prev = p;
  }
  return out;
}

function PagerLink({
  search,
  disabled,
  label,
  children,
}: {
  search: OffersSearch;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  const base = 'border-border grid size-8 place-items-center rounded-lg border transition-colors';
  if (disabled) {
    return (
      <span aria-disabled className={cn(base, 'text-muted-foreground/40')}>
        {children}
      </span>
    );
  }
  return (
    <Link
      to="/offers"
      search={search}
      aria-label={label}
      className={cn(base, 'text-muted-foreground hover:bg-muted hover:text-foreground')}
    >
      {children}
    </Link>
  );
}

export const Route = createFileRoute('/_app/offers/')({
  validateSearch: (search: Record<string, unknown>): OffersSearch => {
    const result: OffersSearch = {};
    if (typeof search.statut === 'string') result.statut = search.statut;
    if (typeof search.q === 'string') result.q = search.q;
    if (typeof search.page === 'number') result.page = search.page;
    if (typeof search.view === 'string') result.view = search.view;
    return result;
  },
  component: OffersPage,
});

function OffersPage(): React.ReactElement {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const filter = toFilterKey(search.statut);
  const view = toViewKey(search.view);
  const q = (search.q ?? '').trim();
  const page = Math.max(1, search.page ?? 1);

  const { data, isLoading, isFetching } = trpc.offer.listPaged.useQuery(
    {
      statut: filter,
      q: q.length > 0 ? q : undefined,
      page,
      pageSize: PAGE_SIZE,
    },
    { placeholderData: keepPreviousData },
  );
  const isAdmin = data?.isAdminView ?? false;
  const [searchInput, setSearchInput] = React.useState(q);

  const offers = React.useMemo<OfferRow[]>(
    () =>
      (data?.items ?? []).map((o) => ({
        id: o.id,
        title: o.title,
        status: o.status,
        vertical: o.vertical,
        currentVersion: o.currentVersion,
        currentVersionMinor: o.currentVersionMinor,
        isPrivate: o.isPrivate,
        createdAt: o.createdAt,
        requesterEmail: o.requesterEmail,
        bidsCount: o.bidsCount,
      })),
    [data],
  );

  const header = (
    <div>
      <h1 className="text-foreground text-base font-bold tracking-tight">
        {isAdmin ? 'Offres' : 'Mes offres'}
      </h1>
      <p className="text-muted-foreground mt-1 text-xs">
        {isAdmin
          ? 'Toutes les offres de la plateforme, en lecture seule.'
          : 'Gérez et suivez toutes vos offres.'}
      </p>
    </div>
  );

  if (isLoading) {
    return (
      <>
        {header}
        <div className="mt-4 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="border-border bg-card flex items-center justify-between rounded-lg border px-4 py-3.5"
            >
              <Skeleton className="h-4 w-64 rounded" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
          ))}
        </div>
      </>
    );
  }

  const counts: Record<string, number> = {
    all: data?.counts.all ?? 0,
    draft: data?.counts.draft ?? 0,
    published: data?.counts.published ?? 0,
  };

  if (counts.all === 0 && q.length === 0 && filter === 'all') {
    return (
      <>
        {header}
        {isAdmin ? (
          <EmptyList
            icon={<Megaphone className="size-8" weight="regular" />}
            title="Aucune offre"
            description="Aucune offre n'a encore été publiée sur la plateforme."
          />
        ) : (
          <EmptyList
            icon={<Megaphone className="size-8" weight="regular" />}
            title="Lance ta première offre"
            description="Décris ton besoin en langage naturel — l'IA génère un cahier des charges structuré, et tu reçois des devis comparables ligne à ligne."
            action={{
              href: '/offers/new',
              label: 'Créer une offre',
              icon: <Plus className="size-4" weight="bold" />,
            }}
            steps={[
              {
                icon: <PencilSimple className="size-5" weight="regular" />,
                title: 'Décris',
                hint: 'Ton besoin en quelques phrases',
              },
              {
                icon: <Sparkle className="size-5" weight="regular" />,
                title: "L'IA structure",
                hint: 'Un CDC clair en 2 niveaux',
              },
              {
                icon: <Scales className="size-5" weight="regular" />,
                title: 'Compare',
                hint: 'Les devis ligne à ligne',
              },
            ]}
          />
        )}
      </>
    );
  }

  const filtered = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(filtered / PAGE_SIZE));
  const from = filtered === 0 ? 0 : Math.min((page - 1) * PAGE_SIZE + 1, filtered);
  const to = Math.min(page * PAGE_SIZE, filtered);

  const searchWith = (next: {
    statut?: string;
    q?: string;
    page?: number;
    view?: string;
  }): OffersSearch => {
    const result: OffersSearch = {};
    const nextStatut = next.statut ?? filter;
    if (nextStatut !== 'all') result.statut = nextStatut;
    const nextQ = next.q ?? q;
    if (nextQ.length > 0) result.q = nextQ;
    const nextPage = next.page ?? page;
    if (nextPage > 1) result.page = nextPage;
    const nextView = next.view ?? view;
    if (nextView !== 'table') result.view = nextView;
    return result;
  };

  const columns: DataTableColumn<OfferRow>[] = [
    {
      key: 'title',
      header: 'Titre',
      cell: (offer) => (
        <div className="flex min-w-0 items-center gap-2">
          <Link
            to="/offers/$id"
            params={{ id: offer.id }}
            className="hover:text-primary truncate font-medium transition-colors"
          >
            {offer.title || 'Offre sans titre'}
          </Link>
          {offer.isPrivate ? <TableTag label="Privée" /> : null}
        </div>
      ),
    },
    ...(isAdmin
      ? [
          {
            key: 'requester',
            header: 'Requester',
            cell: (offer: OfferRow) => (
              <span className="text-muted-foreground truncate text-xs">{offer.requesterEmail}</span>
            ),
          } satisfies DataTableColumn<OfferRow>,
        ]
      : []),
    {
      key: 'vertical',
      header: 'Vertical',
      cell: (offer) => (
        <TableTag label={verticalLabel(offer.vertical)} dotColor={verticalDot(offer.vertical)} />
      ),
    },
    {
      key: 'version',
      header: 'Version',
      cell: (offer) => (
        <span className="text-muted-foreground font-mono text-xs tabular-nums">
          {formatOfferVersion(offer.currentVersion, offer.currentVersionMinor)}
        </span>
      ),
    },
    {
      key: 'bidsCount',
      header: 'Candidatures',
      align: 'right',
      cell: (offer) => (
        <span
          data-testid="offer-bids-count"
          data-count={offer.bidsCount}
          className={cn(
            'font-mono text-xs tabular-nums',
            offer.bidsCount === 0 ? 'text-faint-foreground' : 'text-foreground',
          )}
        >
          {offer.bidsCount}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      cell: (offer) => {
        const meta = statusMeta(offer.status);
        return <StatusBadge label={meta.label} tone={meta.tone} />;
      },
    },
    {
      key: 'createdAt',
      header: 'Créée le',
      align: 'right',
      cell: (offer) => (
        <span className="text-muted-foreground font-mono text-xs tabular-nums">
          {offer.createdAt.toLocaleDateString('fr-FR')}
        </span>
      ),
    },
  ];

  const toolbar = (
    <>
      <div className="bg-muted inline-flex items-center gap-0.5 rounded-lg p-0.75">
        {FILTERS.map((f) => {
          const active = f.key === filter;
          return (
            <Link
              key={f.key}
              to="/offers"
              search={searchWith({ statut: f.key, page: 1 })}
              className={cn(
                'inline-flex items-center rounded-lg px-3.5 py-1.5 text-xs font-[610] transition-colors',
                active
                  ? 'bg-card text-foreground shadow-[0_1px_2px_hsl(var(--foreground)/0.1)]'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {f.label}
              <span
                className={cn(
                  'ml-1.5 font-mono text-[10px] tabular-nums',
                  active ? 'text-primary' : 'text-faint-foreground',
                )}
              >
                {counts[f.key]}
              </span>
            </Link>
          );
        })}
      </div>
      <div className="ml-auto flex items-center gap-2">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void navigate({
              to: '/offers',
              search: searchWith({ q: searchInput.trim(), page: 1 }),
            });
          }}
          className="flex items-center"
        >
          <div className="border-border focus-within:border-primary/50 flex h-9 items-center gap-2 rounded-lg border px-3 transition-colors">
            <MagnifyingGlass className="text-muted-foreground size-4" weight="regular" />
            <input
              name="q"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Rechercher une offre…"
              className="placeholder:text-muted-foreground w-48 bg-transparent text-sm outline-none"
            />
          </div>
        </form>
        <div className="bg-muted inline-flex items-center gap-0.5 rounded-lg p-0.5">
          <Link
            to="/offers"
            search={searchWith({ view: 'table' })}
            aria-label="Vue tableau"
            aria-current={view === 'table' ? 'true' : undefined}
            className={cn(
              'grid size-8 place-items-center rounded-md transition-colors',
              view === 'table'
                ? 'bg-card text-foreground shadow-[0_1px_2px_hsl(var(--foreground)/0.1)]'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Rows className="size-4" weight={view === 'table' ? 'bold' : 'regular'} />
          </Link>
          <Link
            to="/offers"
            search={searchWith({ view: 'grid' })}
            aria-label="Vue cartes"
            aria-current={view === 'grid' ? 'true' : undefined}
            className={cn(
              'grid size-8 place-items-center rounded-md transition-colors',
              view === 'grid'
                ? 'bg-card text-foreground shadow-[0_1px_2px_hsl(var(--foreground)/0.1)]'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <SquaresFour className="size-4" weight={view === 'grid' ? 'bold' : 'regular'} />
          </Link>
        </div>
      </div>
    </>
  );

  const footer = (
    <>
      <span className="text-muted-foreground text-xs">
        Affichage de{' '}
        <b className="text-foreground font-semibold">
          {from}–{to}
        </b>{' '}
        sur <b className="text-foreground font-semibold">{filtered}</b> offre
        {filtered > 1 ? 's' : ''}
      </span>
      {pageCount > 1 ? (
        <div className="flex items-center gap-1">
          <PagerLink
            search={searchWith({ page: page - 1 })}
            disabled={page <= 1}
            label="Page précédente"
          >
            <CaretLeft className="size-4" weight="bold" />
          </PagerLink>
          {pageItems(page, pageCount).map((item) =>
            typeof item === 'number' ? (
              <Link
                key={item}
                to="/offers"
                search={searchWith({ page: item })}
                className={cn(
                  'grid h-8 min-w-8 place-items-center rounded-lg border px-2 text-sm tabular-nums transition-colors',
                  item === page
                    ? 'border-primary bg-primary text-primary-foreground font-semibold'
                    : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                {item}
              </Link>
            ) : (
              <span key={item} className="text-muted-foreground/60 px-1 text-sm" aria-hidden>
                …
              </span>
            ),
          )}
          <PagerLink
            search={searchWith({ page: page + 1 })}
            disabled={page >= pageCount}
            label="Page suivante"
          >
            <CaretRight className="size-4" weight="bold" />
          </PagerLink>
        </div>
      ) : null}
    </>
  );

  const emptyMessage = (
    <p className="text-muted-foreground text-center text-sm">
      Aucune offre ne correspond à cette recherche.
    </p>
  );

  const contentClass = cn('transition-opacity', isFetching && 'opacity-60');

  return (
    <>
      {header}
      {view === 'table' ? (
        <DataTable
          className={contentClass}
          columns={columns}
          rows={offers}
          rowKey={(offer) => offer.id}
          rowAction={(offer) => {
            const action = isAdmin
              ? { href: `/offers/${offer.id}`, label: 'Voir', tone: 'default' as const }
              : actionFor(offer.status, offer.id);
            return <TableActionLink href={action.href} label={action.label} tone={action.tone} />;
          }}
          toolbar={toolbar}
          footer={footer}
          emptyState={emptyMessage}
        />
      ) : (
        <div
          className={cn('border-border bg-card overflow-hidden rounded-2xl border', contentClass)}
        >
          <div className="border-border flex flex-wrap items-center gap-3 border-b px-4 py-3">
            {toolbar}
          </div>
          {offers.length === 0 ? (
            <div className="px-4 py-10">{emptyMessage}</div>
          ) : (
            <ul
              data-testid="offers-grid"
              className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3"
            >
              {offers.map((offer) => {
                const meta = statusMeta(offer.status);
                const action = isAdmin
                  ? { href: `/offers/${offer.id}`, label: 'Voir', tone: 'default' as const }
                  : actionFor(offer.status, offer.id);
                return (
                  <li
                    key={offer.id}
                    className="group border-border bg-background hover:border-primary/40 flex flex-col rounded-xl border p-5 transition-all hover:shadow-[0_8px_24px_-12px_hsl(30_10%_30%/0.15)]"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <StatusBadge label={meta.label} tone={meta.tone} />
                      {offer.isPrivate ? <TableTag label="Privée" /> : null}
                    </div>
                    <Link
                      to="/offers/$id"
                      params={{ id: offer.id }}
                      className="hover:text-primary mt-3 line-clamp-2 text-sm leading-snug font-semibold transition-colors"
                    >
                      {offer.title || 'Offre sans titre'}
                    </Link>
                    {isAdmin ? (
                      <p className="text-muted-foreground mt-1 truncate text-xs">
                        {offer.requesterEmail}
                      </p>
                    ) : null}
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      <TableTag
                        label={verticalLabel(offer.vertical)}
                        dotColor={verticalDot(offer.vertical)}
                      />
                      <BidsCount count={offer.bidsCount} />
                    </div>
                    <div className="border-border/70 mt-4 flex items-center justify-between gap-2 border-t pt-3">
                      <span className="text-muted-foreground font-mono text-xs tabular-nums">
                        {formatOfferVersion(offer.currentVersion, offer.currentVersionMinor)} ·{' '}
                        {offer.createdAt.toLocaleDateString('fr-FR')}
                      </span>
                      <TableActionLink
                        href={action.href}
                        label={action.label}
                        tone={action.tone}
                        className="h-8 min-w-0 px-2.5 text-xs"
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="border-border flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
            {footer}
          </div>
        </div>
      )}
    </>
  );
}
